/**
 * Personal domains for shared-tier customers — the website half of the
 * 2026-10-23 owner decision (contract: "Shared-tier customers: one personal
 * domain with certificates").
 *
 * Free / Starter customers are plain Members of a SYSTEM organisation (dev
 * face: org 3, "Caucasus Systems Basic") that holds unrelated customers. They
 * may add and manage exactly ONE domain of their own, with its records and
 * certificates. dns-manager-api stores it as a *personal* domain (the system
 * org's id, `owner_user_id` = the caller, `personal: true`) and answers:
 *
 *   GET  /dns-manager/domains          → Domain[] + `meta: {scope, limit, used}`
 *   GET  /dns-manager/domains/access   → `{can_manage, scope, limit, used, reason}`
 *   POST /dns-manager/domains (2nd)    → 409 `domain_limit_reached` {limit, plan_hint}
 *
 * Organisation (Team and above) domains keep today's behaviour: the
 * `domain:viewer` probe, the "ask your owner" panel for a Member without a
 * grant, no limit.
 *
 * Pure: relative imports only, `node --test tests/unit/personal-domain.test.mjs`.
 * Every decision about scope, allowance, limit copy and the access panel is
 * made here, not in the SFCs.
 *
 * Author: Matew Makhatadze
 */

export const DOMAIN_SCOPE = Object.freeze({ PERSONAL: 'personal', ORGANIZATION: 'organization' })

export const DOMAIN_ACCESS_PATH = '/dns-manager/domains/access'
export const DOMAIN_LIMIT_CODE = 'domain_limit_reached'
export const DEFAULT_NEXT_PLAN = 'Team'
export const PRICING_PATH = '/pricing'

const SHARED_TIERS = ['free', 'free_trial', 'trial', 'starter', 'basic']

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** 'personal' | 'organization' | null — anything else is not a scope. */
export function normaliseScope(value) {
  const v = String(value === null || value === undefined ? '' : value).trim().toLowerCase()
  if (v === 'personal' || v === 'user') return DOMAIN_SCOPE.PERSONAL
  if (v === 'organization' || v === 'organisation' || v === 'org') return DOMAIN_SCOPE.ORGANIZATION
  return null
}

/** A non-negative whole number, else null (null also means "unlimited" for a limit). */
function count(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
}

/**
 * `{scope, limit, used}` from the list response body, or null when the server
 * said nothing (an older dns-manager). Reads the envelope's top-level `meta`
 * and, defensively, a `meta` block inside `data`.
 */
export function readDomainsMeta(body) {
  if (!body || typeof body !== 'object') return null
  const candidates = [body.meta]
  if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) candidates.push(body.data.meta)
  for (const m of candidates) {
    if (!m || typeof m !== 'object') continue
    const scope = normaliseScope(m.scope)
    if (!scope) continue
    // `decided: false` = dns-manager could not confirm the caller's plan and
    // fell back to showing their own domains; anything but an explicit false is decided.
    return { scope, limit: count(m.limit), used: count(m.used), decided: m.decided !== false }
  }
  return null
}

/**
 * `GET /domains/access` → `{canManage, scope, limit, used, reason}`, or null
 * when the answer is not an access answer (route missing on an older
 * dns-manager, a domain row, junk). `canManage` is true / false, or null
 * when the server did not decide (dns-manager answers `can_manage: null` for
 * an organisation caller whose grant it did not check). `reason` is kept only
 * when it is text.
 */
export function normaliseDomainAccess(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const src = data.access && typeof data.access === 'object' ? data.access : data
  const scope = normaliseScope(src.scope)
  if (!scope) return null
  if (typeof src.can_manage !== 'boolean' && src.can_manage !== null && src.can_manage !== undefined) return null
  if (typeof src.can_manage !== 'boolean' && scope === DOMAIN_SCOPE.PERSONAL) return null
  const reason = typeof src.reason === 'string' && src.reason.trim() ? src.reason.trim() : null
  const canManage = typeof src.can_manage === 'boolean' ? src.can_manage : null
  return { canManage, scope, limit: count(src.limit), used: count(src.used), reason }
}

/**
 * Ask dns-manager whether — and in which scope — this caller may manage
 * domains. Never throws: an access answer, or null (route missing, refused,
 * network) so the caller falls back to the older path.
 */
export async function fetchDomainAccess(http) {
  try {
    const response = await http.get(DOMAIN_ACCESS_PATH)
    const body = response && response.data
    if (body && typeof body === 'object' && body.success === false) return null
    const data = body && typeof body === 'object' && 'data' in body ? body.data : body
    return normaliseDomainAccess(data)
  } catch (_) {
    return null
  }
}

/** The organisation the session is acting in (/me shape). */
function activeOrg(user) {
  const u = user && typeof user === 'object' ? user : null
  if (!u) return null
  const org = u.current_organization || u.default_organization
  return org && typeof org === 'object' ? org : null
}

/**
 * /me says the active organisation is a system (shared) organisation. Used only
 * to word the refusal when an older dns-manager answered no scope — never to
 * pick the personal UI, which follows the server's scope.
 */
export function isSystemOrgUser(user) {
  const org = activeOrg(user)
  return !!org && org.is_system_org === true
}

/**
 * Client-side HINT that the caller is on a shared tier: the active org is a
 * system org, or carries a shared tier. Organisation-level facts only — a
 * user-level `tier` defaults to FREE on some payloads and would mislabel a
 * Team member. The server decides; this only picks which question to ask
 * first — never which UI renders (a Starter customer may own a non-system
 * organisation, which dns-manager treats as an organisation).
 */
export function isSharedTierUser(user) {
  const org = activeOrg(user)
  if (!org) return false
  if (org.is_system_org === true) return true
  const tier = String(org.tier_key || org.tier || '').trim().toLowerCase()
  return SHARED_TIERS.includes(tier)
}

/** The caller's user id from /me (several historic shapes). */
export function userIdOf(user) {
  const u = user && typeof user === 'object' ? user : null
  if (!u) return null
  const id = u.user_id ?? u.id ?? u.userId ?? null
  return id === null || id === undefined || id === '' ? null : String(id)
}

/** True when the row is a personal domain AND it belongs to this caller. */
export function ownsPersonalDomain(domain, user) {
  if (!domain || typeof domain !== 'object' || domain.personal !== true) return false
  const owner = domain.owner_user_id ?? domain.owner?.user_id ?? null
  const me = userIdOf(user)
  return owner !== null && owner !== undefined && me !== null && String(owner) === me
}

/** Rows that occupy a slot (a released / deleted row frees it). */
function occupying(domains) {
  return (Array.isArray(domains) ? domains : []).filter((d) => {
    const s = String((d && (d.state || d.status)) || '').toLowerCase()
    return d && s !== 'deleted' && s !== 'released'
  })
}

/**
 * What the page may offer.
 *   personal  — the caller is in the personal scope
 *   limit     — number, or null (unlimited / unknown)
 *   used      — the larger of the server's count and the rows on screen
 *   canAdd    — the Add button shows
 *   atLimit   — the plan line replaces the Add button
 */
export function domainAllowance({ scope, limit, used, domains } = {}) {
  const personal = normaliseScope(scope) === DOMAIN_SCOPE.PERSONAL
  const lim = count(limit)
  const shown = occupying(domains).length
  const server = count(used)
  const u = Math.max(server === null ? 0 : server, shown)
  const atLimit = personal && lim !== null && u >= lim
  return {
    personal,
    limit: lim,
    used: u,
    remaining: lim === null ? null : Math.max(lim - u, 0),
    atLimit,
    canAdd: !atLimit
  }
}

function titleCase(slug) {
  const s = String(slug || '').trim()
  if (!s) return ''
  return s.split(/[\s_-]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

/**
 * The plan to name in the upgrade line. Accepts the environments-style object
 * (`{next_plan_name, next_tier_key}`) or a bare tier slug; a sentence or
 * anything odd falls back to Team, because the line must stay one plain
 * sentence whatever the server sends.
 */
export function nextPlanName(planHint) {
  if (planHint && typeof planHint === 'object') {
    if (typeof planHint.next_plan_name === 'string' && planHint.next_plan_name.trim()) return planHint.next_plan_name.trim()
    if (typeof planHint.next_tier_key === 'string' && /^[a-z0-9_-]{2,24}$/i.test(planHint.next_tier_key.trim())) return titleCase(planHint.next_tier_key)
    return DEFAULT_NEXT_PLAN
  }
  if (typeof planHint === 'string' && /^[a-z0-9_-]{2,24}$/i.test(planHint.trim())) return titleCase(planHint)
  return DEFAULT_NEXT_PLAN
}

/** "Your plan includes one domain — upgrade to Team for more." */
export function domainLimitLine({ limit = 1, planHint = null } = {}) {
  const n = count(limit)
  const lim = n === null ? 1 : n
  const howMany = lim < NUMBER_WORDS.length ? NUMBER_WORDS[lim] : String(lim)
  return `Your plan includes ${howMany} domain${lim === 1 ? '' : 's'} — upgrade to ${nextPlanName(planHint)} for more.`
}

/** 409 `domain_limit_reached` from the claim endpoint. Carries the plain sentence as its message. */
export class DomainLimitReached extends Error {
  constructor({ limit = 1, planHint = null } = {}) {
    super(domainLimitLine({ limit, planHint }))
    this.name = 'DomainLimitReached'
    this.limitReached = true
    this.limit = count(limit) === null ? 1 : count(limit)
    this.planHint = planHint
  }
}

/**
 * `DomainLimitReached` for a 409 `domain_limit_reached` (code from
 * `error_code` / `error` / `code`, facts from `details` / `data` / the top
 * level), else null.
 */
export function domainLimitError(err) {
  const resp = err && err.response
  if (!resp || resp.status !== 409) return null
  const body = resp.data && typeof resp.data === 'object' ? resp.data : {}
  const code = body.error_code || (typeof body.error === 'string' ? body.error : null) || body.code || null
  const facts = [body.details, body.detail, body.data, body.errors, body].find((x) => x && typeof x === 'object' && !Array.isArray(x) &&
    ('limit' in x || 'plan_hint' in x || x.error_code === DOMAIN_LIMIT_CODE)) || {}
  const factCode = facts.error_code || facts.code || null
  if (code !== DOMAIN_LIMIT_CODE && factCode !== DOMAIN_LIMIT_CODE) return null
  return new DomainLimitReached({ limit: facts.limit ?? 1, planHint: facts.plan_hint ?? null })
}

const REASON_COPY = {
  trial_expired: 'Your free trial has ended — choose a plan to keep managing your domain.',
  plan_required: 'Domains need an active plan. Choose one to add your domain.',
  account_locked: 'Your account is locked, so domains can’t be changed right now.',
  organization_locked: 'This organisation is locked, so domains can’t be changed right now.',
  not_shared_tier: 'Domains in this organisation are managed by its owner and admins.',
  personal_domains_disabled: 'Personal domains aren’t switched on for this part of the platform yet.',
  undecided: 'We couldn’t confirm your plan just now. Try again in a moment.',
  access_undecided: 'We couldn’t confirm your plan just now. Try again in a moment.',
  no_org_context: 'Choose an organisation first — domains belong to the organisation you are working in.',
  limit_reached: 'Your plan includes one domain, and it is already in use.',
  organization_member: 'Domains in this organisation are managed by its owner and admins.',
  system_org: 'Your plan includes one domain of your own.',
  operator: 'You manage domains as a platform operator.'
}

/** The quiet line when dns-manager could not confirm the plan (`meta.decided === false`). */
export const UNDECIDED_NOTE = 'We couldn’t confirm your plan just now, so this page shows only your own domains. Refresh in a moment.'

/** Claim refusals other than the limit, as plain sentences (dns-manager 2.2.0 codes). */
export const PLATFORM_ZONE_MESSAGE = 'That name belongs to the platform, so it can’t be added as your own domain. Add a domain you own instead.'
export const ACCESS_UNDECIDED_MESSAGE = 'We couldn’t confirm your plan just now. Try adding the domain again in a moment.'
export const NOT_VERIFIED_MESSAGE = 'Verify the domain first — publish the TXT record and check it, then manage its records.'

/**
 * The server's `reason` as one plain sentence. A known code gets its copy, a
 * readable sentence is shown as it is, anything else (a bare unknown code,
 * JSON, a stack) is replaced by the generic line — never raw.
 */
export function plainReason(reason, fallback = 'Domains aren’t available on your plan right now.') {
  if (typeof reason !== 'string') return fallback
  const r = reason.trim()
  if (!r) return fallback
  if (REASON_COPY[r.toLowerCase()]) return REASON_COPY[r.toLowerCase()]
  if (/^[{[<]/.test(r) || /Traceback|Exception|\bat \w+\.|\n/.test(r)) return fallback
  if (/^[a-z0-9_.:-]+$/i.test(r)) return fallback
  return r.length > 240 ? fallback : r
}

export const ORG_ACCESS_TITLE = 'You don’t have domain access in this organisation'

/**
 * The refusal panel, by who is refused.
 *   organisation member without a grant → today's title + the grant note
 *   personal scope, can_manage false    → the server's reason in plain words
 *   system org on an older dns-manager  → "not switched on here yet"; never
 *                                         "ask your owner" (the owner of a
 *                                         system org is the platform)
 */
export function accessPanel({ scope = null, systemOrg = false, reason = null, orgNote = '' } = {}) {
  const s = normaliseScope(scope)
  if (s === DOMAIN_SCOPE.PERSONAL) {
    return {
      kind: 'personal',
      title: 'Your domain isn’t available right now',
      note: plainReason(reason),
      showPricing: true
    }
  }
  if (!s && systemOrg) {
    return {
      kind: 'personal-unavailable',
      title: 'Your own domain is on its way',
      note: 'Your plan includes one domain of your own, with its DNS records and certificates. It isn’t switched on in this part of the platform yet — check back soon.',
      showPricing: false
    }
  }
  return { kind: 'organization', title: ORG_ACCESS_TITLE, note: orgNote, showPricing: false }
}

/**
 * Decide how `/my/domains` loads. `api` = `{probe(), access(), listWithMeta()}`
 * (probe → true/false/null; access → normalised or null; listWithMeta →
 * `{domains, meta}` and may throw `denied`-flagged errors).
 *
 * dns-manager decides personal vs organisation by SYSTEM ORG only, so its
 * answer (`/domains/access` scope, then the list's `meta.scope`) is the only
 * thing that picks the UI. `sharedTier` (the free / starter / trial guess)
 * only picks which request goes first; `systemOrg` (/me `is_system_org`) only
 * words the refusal when an older dns-manager answered no scope at all.
 *
 *   super-admin           → list (no probe; the permission plane lets them through)
 *   organisation (guess)  → probe as today; a `false` asks access once (a
 *                           shared-tier caller /me did not reveal), else refused
 *   shared tier (guess)   → access first:
 *                             personal      → list, no check-batch
 *                             organization  → today's org path (probe, then list)
 *                             no answer     → system org: list (meta may carry
 *                                             the scope); otherwise the probe
 *
 * Returns `{denied, scope, limit, used, reason, systemOrg, decided, domains}`;
 * a refusal from the list is reported as `denied: true`, any other error rethrows.
 */
export async function loadDomainsScoped(api, { superAdmin = false, sharedTier = false, systemOrg = false, isDenied = () => false } = {}) {
  let access = null
  if (!superAdmin) {
    let allowed = null
    if (sharedTier) access = await api.access()
    const personalAnswer = !!(access && access.scope === DOMAIN_SCOPE.PERSONAL)
    const orgPath = !sharedTier || (access ? !personalAnswer : !systemOrg)
    if (orgPath) allowed = await api.probe()
    if (!sharedTier && allowed === false) access = await api.access()
    if (access && access.canManage === false) {
      return { denied: true, scope: access.scope, limit: access.limit, used: access.used, reason: access.reason, systemOrg, decided: true, domains: [] }
    }
    // Refused by the probe and not rescued by a personal answer.
    if (allowed === false && !(access && access.scope === DOMAIN_SCOPE.PERSONAL)) {
      return { denied: true, scope: access ? access.scope : null, limit: null, used: null, reason: null, systemOrg, decided: true, domains: [] }
    }
  }
  try {
    const { domains, meta } = await api.listWithMeta()
    const scope = (meta && meta.scope) || (access && access.scope) || DOMAIN_SCOPE.ORGANIZATION
    const limit = meta && meta.limit !== null ? meta.limit : (access ? access.limit : null)
    const used = meta && meta.used !== null ? meta.used : (access ? access.used : null)
    const decided = !(meta && meta.decided === false)
    return { denied: false, scope, limit, used, reason: access ? access.reason : null, systemOrg, decided, domains: Array.isArray(domains) ? domains : [] }
  } catch (err) {
    if (isDenied(err)) {
      return { denied: true, scope: access ? access.scope : null, limit: null, used: null, reason: null, systemOrg, decided: true, domains: [] }
    }
    throw err
  }
}
